<template>
  <div :class="$style.reports">
    <reports-title :title="pageTitle" />
    <reports-toolbar
      :toolbar-disabled="isStub"
      :report-title="pageTitle"
      :toolbar-year-switch-data="toolbarYearSwitchData"
      @setYear="setYear"
    />
    <skeleton-wrapper
      v-if="isShowSkeleton"
      id="skeletonWrapper"
    >
      <template #body>
        <div :class="[$style.chart_wrapper, $style.chart_wrapper_scatter, isShowSkeleton && !projectsMilestones.total ? $style.stub : '']">
          <milestones-chart
            id="report_export_id"
            :data="stubMilestones"
            :current-year="toolbarYearSwitchData.currentYear"
            :small="small"
            :show-stub-message="false"
          />
        </div>
      </template>
    </skeleton-wrapper>
    <div
      v-else
      :class="[$style.chart_wrapper, $style.chart_wrapper_scatter, isStub && !projectsMilestones.total ? $style.stub : '']"
    >
      <milestones-chart
        id="report_export_id"
        :data="projectsMilestones"
        :current-year="toolbarYearSwitchData.currentYear"
        :small="small"
        :all-statuses="allStatuses"
        @dataExists="checkData"
        @setYearRange="setYearRange"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import constants from '$$helpers/constants';
import dateHelper from '$$helpers/dateFormats';
import projects from '$$projectModel';
import multiViewsProjects from '../../../models/multiViewsProjects';
import reportsTitle from '../include/title.vue';
import reportsToolbar from '../include/toolbar.vue';
import MilestonesChart from '$$vueCmp/charts/MilestonesChart.vue';
import reportsConstants from '../include/constants';
import projectsModel from '../../../models/projects';
import skeletonWrapper from '../../common/VueComponents/skeletons/skeletonWrapper.vue';

export default {
  components: {
    MilestonesChart,
    reportsTitle,
    reportsToolbar,
    skeletonWrapper,
  },
  data() {
    return {
      projectsMilestones: {
        items: [],
        total: 0,
        overdue: 0,
        milestones: [],
      },
      stubMilestones: {
        items: [],
        total: 0,
        overdue: 0,
        milestones: [],
      },
      projectIds: [],
      multiviewProjectIds: [],
      type: null,
      locale: __,
      toolbarYearSwitchData: {
        currentYear: new Date().getFullYear(),
        minYear: new Date().getFullYear(),
        maxYear: new Date().getFullYear(),
      },
      small: false,
      isStub: true,
      isShowSkeleton: true,
    };
  },
  computed: {
    currentProjectIds() {
      const projectIds = [this.projectIds, this.multiviewProjectIds].find(arr => arr.length) || [];

      if (!projectIds.length) {
        return projectsModel.getAllProjects().map(project => project.gantt_id);
      }

      return projectIds;
    },
    jiraStatuses() {
      const statuses = {};

      this.currentProjectIds.forEach(projectId => {
        const projectData = projectsModel.getProjectDataById(projectId);

        if (projectData && projectData.is_jira) {
          const jiraStatusesData = this.$store.getters['columns/getJiraColumnByNameAndProjectId']('status', projectId);

          if (jiraStatusesData && jiraStatusesData.options.length) {
            jiraStatusesData.options.forEach(status => {
              const { id, name, color } = status;
              const hasOption = Object.keys(statuses).find(i => i === id);

              if (!hasOption) {
                statuses[id] = {
                  color,
                  name,
                };
              }
            });
          }
        }
      });

      return statuses;
    },
    allStatuses() {
      return {
        ...constants.TASK_STATUSES,
        ...this.jiraStatuses,
      };
    },
    pageTitle() {
      return reportsConstants.TITLE.progress.milestone;
    },
    currentFilter() {
      return this.$store.getters['filter/currentFilter'];
    },
    tasks() {
      return this.$store.getters['tasksModel/getTasksByGanttIds'](this.currentProjectIds)
        .filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);
    },
    milestones() {
      return this.tasks.filter(task => task.type === constants.TASK_TYPES.milestone);
    },
    taskStatusesColors() {
      const colors = {
        1: '#999999',
        2: '#FF9A00',
        3: '#4484CD',
        4: '#8BC34A',
      };

      Object.entries(this.allStatuses).forEach(([key, value]) => {
        colors[key] = value.color;
      });

      return colors;
    },
    taskStatuses() {
      return Object.entries(this.allStatuses).map(([key, value]) => ({
        id: +key,
        color: this.taskStatusesColors[key],
        name: value.locale ? this.locale(value.locale) : value.name,
      }));
    },
    tasksGroupedByStatus() {
      return this.tasks.reduce((acc, curr) => {
        (acc[+curr.status] = acc[+curr.status] || []).push(curr);

        return acc;
      }, {});
    },
    milestonesGroupedByStatus() {
      return this.milestones.reduce((acc, curr) => {
        (acc[+curr.status] = acc[+curr.status] || []).push(curr);

        return acc;
      }, {});
    },
    tasksByStatusesData() {
      const data = {
        items: [],
        summary: {
          total: this.summary.totalTasks,
          unassigned: this.summary.unassignedTasks,
          overdue: this.summary.overdueTasks,
        },
      };

      if (!this.tasks.length) {
        return data;
      }

      data.items = this.taskStatuses.map(status => {
        const statusTasks = this.tasksGroupedByStatus[status.id];

        if (!statusTasks || !statusTasks.length) {
          return null;
        }

        const qty = statusTasks.length;
        const value = Math.round((qty / this.tasks.length) * 100);

        return { ...status, qty, value };
      }).filter(item => item);

      data.summary.total.value = this.tasks.length;
      data.summary.unassigned.value = this.tasks.filter(task => !task.resources.length).length;
      data.summary.overdue.value = this.tasks.filter(task => task.progress < 1 && moment(task.end_date).isBefore()).length;

      return data;
    },
    sourceType() {
      if (this.multiviewProjectIds.length) {
        return 'multiview';
      }

      return 'projects';
    },
  },
  watch: {
    'currentFilter.properties.gantt_id': function (projectIds) {
      if (projectIds?.length) {
        this.projectIds = [...projectIds];
      } else {
        this.projectIds = [];
      }
    },
    'currentFilter.properties.portfolio_id': function (portfolioIds) {
      if (portfolioIds?.length) {
        const multiviewData = multiViewsProjects.getProjectDataById(portfolioIds[0]);

        this.multiviewProjectIds = [...multiviewData.ganttIDs];
      } else {
        this.multiviewProjectIds = [];
      }
    },
    currentProjectIds() {
      this.init();
      setTimeout(() => {
        this.isShowSkeleton = false;
      }, 0);
    },
  },
  mounted() {
    const reportNameAnalytics = this.pageTitle.replace(/^exmp_chart_/, '').replace(/_title$/, '');

    userExtAnalytics.log('report_open', {
      report_type: reportNameAnalytics,
      source_type: this.sourceType,
      projects_selected_count: this.currentProjectIds?.length,
    });

    if (this.currentFilter?.properties?.gantt_id?.length) {
      this.projectIds = [...this.currentFilter.properties.gantt_id];
    }

    if (this.currentFilter?.properties?.portfolio_id?.length) {
      const multiviewData = multiViewsProjects.getProjectDataById(this.currentFilter?.properties?.portfolio_id[0]);

      if (multiviewData) {
        this.multiviewProjectIds = [...multiviewData.ganttIDs];
      }
    }

    const ids = [this.projectIds, this.multiviewProjectIds].find(arr => arr.length) || [];

    if (!ids.length) {
      this.projectIds = projectsModel.getAllProjects().map(project => project.gantt_id);
    }

    this.small = window.screen.height <= 768;
  },
  methods: {
    init() {
      const data = {
        items: [],
        total: 0,
        overdue: 0,
        milestones: [],
      };

      if (!this.milestones.length) {
        this.projectsMilestones = data;

        return;
      }

      data.items = this.taskStatuses.map(status => {
        const statusMilestones = this.milestonesGroupedByStatus[status.id];

        if (!statusMilestones || !statusMilestones.length) {
          return null;
        }

        const qty = statusMilestones.length;
        const value = Math.round((qty / this.milestones.length) * 100);

        return { ...status, qty, value };
      }).filter(item => item);

      data.total = this.milestones.length;
      data.overdue = this.milestones.filter(milestone => milestone.progress < 1 && moment(milestone.start_date).isBefore()).length;
      data.milestones = this.milestones.map(milestone => {
        const status = this.allStatuses[+milestone.status];
        const statusColor = this.taskStatusesColors[+milestone.status];
        const statusName = status.locale ? this.locale(status.locale) : status.name;
        const formatDate = dateHelper.formatDateNoTime(milestone.start_date);
        const projectName = projects.getProjectDataById(milestone.gantt_id).name;

        return {
          ...milestone,
          statusColor,
          statusName,
          formatDate,
          projectName,
        };
      }).sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

      this.projectsMilestones = data;
    },
    setYear(year) {
      if (year < this.toolbarYearSwitchData.minYear || year > this.toolbarYearSwitchData.maxYear) {
        return;
      }

      this.toolbarYearSwitchData.currentYear = year;
    },
    checkData(num) {
      this.isStub = !(num > 0);
    },
    setYearRange({ minYear, maxYear }) {
      this.toolbarYearSwitchData.minYear = minYear;
      this.toolbarYearSwitchData.maxYear = maxYear;

      if (minYear > this.toolbarYearSwitchData.currentYear) {
        this.toolbarYearSwitchData.currentYear = minYear;
      } else if (maxYear < this.toolbarYearSwitchData.currentYear) {
        this.toolbarYearSwitchData.currentYear = maxYear;
      }
    },
  },
};
</script>

<style module src='./../less/reportsMain.less'></style>
